import { useState, useEffect } from "react";
import { useMediaQuery } from "@mantine/hooks";
import { Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
     AppShell,
     Group,
     Title,
     Button,
     Text,
     Menu,
     UnstyledButton,
     Avatar,
     Divider,
     ScrollArea,
     Badge,
     Indicator,
     Tooltip,
     Stack,
     Box,
     Alert,
} from "@mantine/core";
import {
     IconWallet,
     IconHistory,
     IconId,
     IconChevronDown,
     IconLogout,
     IconMenu2,
     IconUserCheck,
     IconBell,
     IconMail,
     IconAlertCircle,
     IconX,
     IconCurrencyDollar,
     IconShieldLock,
} from "@tabler/icons-react";
import { useAuth } from "../contexts/AuthContext";
import { AppName, Logo, HttpCodes } from "../lib/data";
import { Endpoints } from "../lib/endpoints";
import { usePerformRequest, PerformRequest } from "../lib/usePerformRequest";
import { NonPaginatedResponse } from "../types";
import { INotificationProps } from "../types/Notification";
import { useToastNotification } from "../hooks/useToastNotification";

interface NavItem {
     label: string;
     path: string;
     icon: any;
}

const navItems: NavItem[] = [
     { label: "Profile", path: "/dashboard/profile", icon: IconUserCheck },
     { label: "Account", path: "/dashboard/account", icon: IconShieldLock },
     {
          label: "Request Loan",
          path: "/dashboard/request-loan",
          icon: IconWallet,
     },
     {
          label: "Repay Loan",
          path: "/dashboard/loan-repayment",
          icon: IconCurrencyDollar,
     },
     {
          label: "Loan History",
          path: "/dashboard/loan-history",
          icon: IconHistory,
     },
     { label: "KYC", path: "/dashboard/kyc", icon: IconId },
];

export default function DashboardLayout() {
     const { user, logout, reloadUser } = useAuth();
     const navigate = useNavigate();
     const location = useLocation();
     const [menuOpened, setMenuOpened] = useState(false);
     const [notificationsMenuOpened, setNotificationsMenuOpened] = useState(false);
     const [isResendingEmail, setIsResendingEmail] = useState(false);
     const [bannerDismissed, setBannerDismissed] = useState(false);
     const { success, error: showError } = useToastNotification();

     // Use a custom breakpoint: treat screens below 1450px as "small" (show compact navbar)
     const isLargeScreen = useMediaQuery("(min-width: 1450px)");

     const {
          data: notificationsResponse,
          reloadData: reloadNotifications,
     } = usePerformRequest<NonPaginatedResponse<INotificationProps[]>>({
          method: Endpoints.GetNotifications().method,
          route: Endpoints.GetNotifications().route,
     });

     const notifications = notificationsResponse?.data ?? [];
     const unreadCount = notifications.filter((notification) => !notification.isRead).length;

     const handleLogout = async () => {
          try {
               await logout();
               navigate("/login");
          } catch (error) {
               console.error("Logout error:", error);
          }
     };

     const handleNavigate = (path: string) => {
          navigate(path);
          setMenuOpened(false);
     };

     const isActive = (path: string) => location.pathname === path;

     const formatNotificationTime = (timestamp?: number) => {
          if (!timestamp) return "";
          return new Date(timestamp).toLocaleString("en-US", {
               month: "short",
               day: "numeric",
               hour: "2-digit",
               minute: "2-digit",
          });
     };

     const handleNotificationMenuChange = async (opened: boolean) => {
          setNotificationsMenuOpened(opened);
          if (opened && unreadCount > 0) {
               await PerformRequest({
                    method: Endpoints.MarkNotificationsAsRead().method,
                    route: Endpoints.MarkNotificationsAsRead().route,
               });
               reloadNotifications();
          }
     };

     const handleResendVerificationEmail = async () => {
          setIsResendingEmail(true);
          try {
               const response = await PerformRequest<NonPaginatedResponse<any>>({
                    method: Endpoints.GetResendVerificationEmail().method,
                    route: Endpoints.GetResendVerificationEmail().route,
               });

               if (response && response.status === HttpCodes.Success) {
                    success("Verification email sent! Please check your inbox.");
                    // Reload user after a short delay to check if they verified
                    setTimeout(() => {
                         reloadUser();
                    }, 2000);
               } else {
                    showError(response?.message || "Failed to send verification email. Please try again.");
               }
          } catch (err: any) {
               showError(err?.message || "Failed to send verification email. Please try again.");
          } finally {
               setIsResendingEmail(false);
          }
     };

     const showEmailBanner = user && !user.emailVerified && !bannerDismissed;

     // Reset banner dismissed state when email is verified
     useEffect(() => {
          if (user?.emailVerified && bannerDismissed) {
               setBannerDismissed(false);
          }
     }, [user?.emailVerified, bannerDismissed]);

     return (
          <AppShell header={{ height: 70 }} padding="md">
               <AppShell.Header
                    style={{
                         background: "white",
                         borderBottom: "1px solid #E0E0E0",
                    }}
               >
                    <Group h="100%" px={{ base: "md", sm: "lg", md: "xl" }} justify="space-between" wrap="nowrap">
                         {/* Logo */}
                         <Link to="/" style={{ textDecoration: "none" }}>
                              <Group gap="sm">
                                   <img
                                        src={Logo}
                                        alt={`${AppName} Logo`}
                                        style={{ height: "40px", width: "auto" }}
                                   />
                                   <Title
                                        order={3}
                                        style={{ color: "#679369" }}
                                        className="font-bold"
                                        visibleFrom="xs"
                                   >
                                        {AppName}
                                   </Title>
                              </Group>
                         </Link>

                         {/* Navigation Menu - Desktop (shows only from 1450px and above) */}
                         {isLargeScreen && (
                              <Group gap="xs" wrap="nowrap">
                                   {navItems.map((item) => {
                                        const IconComponent = item.icon;
                                        const active = isActive(item.path);

                                        return (
                                             <Button
                                                  key={item.path}
                                                  variant={
                                                       active ? "filled" : "subtle"
                                                  }
                                                  leftSection={
                                                       <IconComponent size={18} />
                                                  }
                                                  onClick={() =>
                                                       handleNavigate(item.path)
                                                  }
                                                  size="sm"
                                                  styles={{
                                                       root: {
                                                            backgroundColor: active
                                                                 ? "#679369"
                                                                 : "transparent",
                                                            color: active
                                                                 ? "white"
                                                                 : "#679369",
                                                            border: active
                                                                 ? "none"
                                                                 : "1px solid transparent",
                                                            "&:hover": {
                                                                 backgroundColor:
                                                                      active
                                                                           ? "#679369"
                                                                           : "#F0F9F0",
                                                                 color: active
                                                                      ? "white"
                                                                      : "#679369",
                                                                 border: "1px solid #679369",
                                                            },
                                                            transition:
                                                                 "all 0.2s ease",
                                                       },
                                                  }}
                                             >
                                                  {item.label}
                                             </Button>
                                        );
                                   })}
                              </Group>
                         )}

                    {/* Mobile Menu & User Menu */}
                    <Group gap="sm" wrap="nowrap">
                              {/* Mobile Menu (shows below 1450px) */}
                              {!isLargeScreen && (
                                   <Menu
                                        shadow="md"
                                        width={200}
                                        opened={menuOpened}
                                        onChange={setMenuOpened}
                                        position="bottom-end"
                                        trigger="click"
                                        closeOnItemClick
                                   >
                                        <Menu.Target>
                                             <UnstyledButton style={{ padding: "8px" }}>
                                                  <IconMenu2
                                                       size={24}
                                                       style={{ color: "#679369" }}
                                                  />
                                             </UnstyledButton>
                                        </Menu.Target>

                                   <Menu.Dropdown>
                                        {navItems.map((item) => {
                                             const IconComponent = item.icon;
                                             const active = isActive(item.path);

                                             return (
                                                  <Menu.Item
                                                       key={item.path}
                                                       leftSection={
                                                            <IconComponent
                                                                 size={18}
                                                            />
                                                       }
                                                       onClick={() =>
                                                            handleNavigate(
                                                                 item.path
                                                            )
                                                       }
                                                       style={{
                                                            backgroundColor:
                                                                 active
                                                                      ? "#F0F9F0"
                                                                      : "transparent",
                                                            color: active
                                                                 ? "#679369"
                                                                 : "#333",
                                                            fontWeight: active
                                                                 ? 600
                                                                 : 400,
                                                       }}
                                                       styles={{
                                                            item: {
                                                                 "&:hover": {
                                                                      backgroundColor:
                                                                           "#F0F9F0",
                                                                      color: "#679369",
                                                                      border: "1px solid #679369",
                                                                 },
                                                                 transition:
                                                                      "all 0.2s ease",
                                                            },
                                                       }}
                                                  >
                                                       {item.label}
                                                  </Menu.Item>
                                             );
                                        })}
                                   </Menu.Dropdown>
                                   </Menu>
                              )}

                          {/* Notifications */}
                          <Menu
                               shadow="md"
                               width={320}
                               position="bottom-end"
                               opened={notificationsMenuOpened}
                               onChange={handleNotificationMenuChange}
                          >
                               <Menu.Target>
                                    <Tooltip label="Notifications">
                                        <UnstyledButton>
                                             <Indicator
                                                  disabled={unreadCount === 0}
                                                  label={unreadCount > 9 ? "9+" : unreadCount}
                                                  size={16}
                                                  color="green"
                                                  offset={4}
                                             >
                                                  <IconBell size={22} style={{ color: "#679369" }} />
                                             </Indicator>
                                        </UnstyledButton>
                                    </Tooltip>
                               </Menu.Target>
                               <Menu.Dropdown p="sm">
                                    <Stack gap="xs">
                                         <Group justify="space-between" align="center">
                                              <Text fw={600} size="sm">
                                                   Notifications
                                              </Text>
                                              {unreadCount > 0 && (
                                                   <Badge color="green" size="sm">
                                                        {unreadCount} new
                                                   </Badge>
                                              )}
                                         </Group>
                                         {notifications.length === 0 ? (
                                              <Text size="sm" c="dimmed">
                                                   You're all caught up.
                                              </Text>
                                         ) : (
                                              <ScrollArea h={260} type="auto">
                                                   <Stack gap="sm" pr="xs">
                                                        {notifications.map((notification) => (
                                                             <Box
                                                                  key={notification.id}
                                                                  p="sm"
                                                                  style={{
                                                                       border: "1px solid #E5E7EB",
                                                                       borderRadius: "8px",
                                                                       backgroundColor: notification.isRead
                                                                            ? "white"
                                                                            : "#F0FDF4",
                                                                  }}
                                                             >
                                                                  <Group justify="space-between" align="center" mb={4}>
                                                                       <Text fw={600} size="sm">
                                                                            {notification.title}
                                                                       </Text>
                                                                       <Badge size="xs" variant="light" color="green">
                                                                            {notification.type.toUpperCase()}
                                                                       </Badge>
                                                                  </Group>
                                                                  <Text size="sm" c="dimmed">
                                                                       {notification.message}
                                                                  </Text>
                                                                  <Text size="xs" c="dimmed" mt={6}>
                                                                       {formatNotificationTime(notification.createdAt)}
                                                                  </Text>
                                                             </Box>
                                                        ))}
                                                   </Stack>
                                              </ScrollArea>
                                         )}
                                    </Stack>
                               </Menu.Dropdown>
                          </Menu>

                              {/* User Menu */}
                              <Menu
                                   shadow="md"
                                   width={200}
                                   position="bottom-end"
                              >
                                   <Menu.Target>
                                        <UnstyledButton>
                                             <Group gap="xs">
                                                  <Avatar
                                                       src={user?.avatar || user?.bvnImage}
                                                       size={36}
                                                       radius="xl"
                                                       style={{
                                                            background: user?.avatar || user?.bvnImage 
                                                                 ? undefined 
                                                                 : "#679369",
                                                            color: "white",
                                                       }}
                                                  >
                                                       {!(user?.avatar || user?.bvnImage) && (
                                                            <Text c="white" size="sm">
                                                                 {user?.firstName?.[0]?.toUpperCase() || 
                                                                  user?.email?.[0]?.toUpperCase() ||
                                                                  "U"}
                                                            </Text>
                                                       )}
                                                  </Avatar>
                                                  <Text
                                                       size="sm"
                                                       fw={500}
                                                       visibleFrom="sm"
                                                       style={{ 
                                                            color: "#333",
                                                            maxWidth: "200px",
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap"
                                                       }}
                                                  >
                                                       {user?.email}
                                                  </Text>
                                                  <Box visibleFrom="xs">
                                                       <IconChevronDown
                                                            size={16}
                                                            style={{ color: "#666" }}
                                                       />
                                                  </Box>
                                             </Group>
                                        </UnstyledButton>
                                   </Menu.Target>

                                   <Menu.Dropdown>
                                        <Menu.Label py={8}>
                                             <Group gap="sm">
                                                  <Avatar
                                                       src={user?.avatar || user?.bvnImage}
                                                       size="sm"
                                                       radius="xl"
                                                  >
                                                       {!(user?.avatar || user?.bvnImage) && (
                                                            <Text size="xs">
                                                                 {user?.firstName?.[0]?.toUpperCase() || 
                                                                  user?.email?.[0]?.toUpperCase() ||
                                                                  "U"}
                                                            </Text>
                                                       )}
                                                  </Avatar>
                                                  <div>
                                                       <Text size="sm" fw={600}>
                                                            {user?.firstName} {user?.lastName}
                                                       </Text>
                                                       <Text size="xs" c="dimmed">
                                                            {user?.email}
                                                       </Text>
                                                  </div>
                                             </Group>
                                        </Menu.Label>
                                        <Divider />
                                        <Menu.Item
                                             py={10}
                                             leftSection={
                                                  <IconLogout size={18} />
                                             }
                                             onClick={handleLogout}
                                             color="red"
                                        >
                                             Logout
                                        </Menu.Item>
                                   </Menu.Dropdown>
                              </Menu>
                         </Group>
                    </Group>
               </AppShell.Header>

               <AppShell.Main style={{ background: "#FAFAFA" }}>
                    {showEmailBanner && (
                         <Alert
                              icon={<IconAlertCircle size={20} />}
                              title="Email Verification Required"
                              color="yellow"
                              radius="md"
                              mb="md"
                              style={{
                                   border: "2px solid #f59e0b",
                                   backgroundColor: "#fffbeb",
                              }}
                              styles={{
                                   root: {
                                        position: "sticky",
                                        top: 0,
                                        zIndex: 100,
                                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                                   },
                              }}
                         >
                              <Group justify="space-between" align="flex-start" wrap="wrap">
                                   <Box style={{ flex: 1, minWidth: "250px" }}>
                                        <Text size="sm" fw={500} mb="xs">
                                             Please verify your email address to access all features
                                        </Text>
                                        <Text size="xs" c="dimmed">
                                             Check your inbox for the verification link. If you didn't receive it, you can request a new one.
                                        </Text>
                                   </Box>
                                   <Group gap="xs" wrap="nowrap">
                                        <Button
                                             size="sm"
                                             leftSection={<IconMail size={16} />}
                                             onClick={handleResendVerificationEmail}
                                             loading={isResendingEmail}
                                             variant="filled"
                                             style={{
                                                  backgroundColor: "#679369",
                                             }}
                                        >
                                             {isResendingEmail ? "Sending..." : "Resend Email"}
                                        </Button>
                                        <UnstyledButton
                                             onClick={() => setBannerDismissed(true)}
                                             style={{
                                                  padding: "4px",
                                                  display: "flex",
                                                  alignItems: "center",
                                                  justifyContent: "center",
                                             }}
                                        >
                                             <IconX size={18} style={{ color: "#666" }} />
                                        </UnstyledButton>
                                   </Group>
                              </Group>
                         </Alert>
                    )}
                    <AnimatePresence mode="wait">
                         <motion.div
                              key={location.pathname}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                              transition={{ duration: 0.2 }}
                         >
                              <Outlet />
                         </motion.div>
                    </AnimatePresence>
               </AppShell.Main>
          </AppShell>
     );
}
